import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Bookmark, BookmarkActionViewMode } from '../../types/api';
import {
  useLikeBookmark,
  useArchiveBookmark,
  useUnarchiveBookmark,
  useDeleteBookmark,
  useMoveToFolder,
} from '../../services/bookmarkActions';
import { useCurrentUser } from '../../services/userHooks';
import { useBookmarkEdit } from '../../contexts/BookmarkEditContext';
import { useBookmarkPageContent } from '../../contexts/BookmarkPageContext';
import { useNavigateToNextBookmark } from '../../hooks/useNavigateToNextBookmark';
import FolderMenu from './FolderMenu';
import MenuItem from './MenuItem';
import ReaderTagPopover from '../Reader/ReaderTagPopover';
import Icon from '../ui/Icon';

interface BookmarkActionsMenuProps {
  bookmark: Bookmark;
  viewMode?: BookmarkActionViewMode;
  currentFolderId?: string;
  onClose: () => void;
}

function BookmarkActionsMenu({
  bookmark,
  viewMode = 'home',
  currentFolderId,
  onClose,
}: BookmarkActionsMenuProps) {
  const { currentUser: userData } = useCurrentUser();
  const likeMutation = useLikeBookmark();
  const archiveMutation = useArchiveBookmark();
  const unarchiveMutation = useUnarchiveBookmark();
  const deleteMutation = useDeleteBookmark();
  const moveMutation = useMoveToFolder();

  const [isLiked, setIsLiked] = useState(bookmark.liked);
  const [showFolderMenu, setShowFolderMenu] = useState(false);
  const [showTagPopover, setShowTagPopover] = useState(false);
  const { openEditModal } = useBookmarkEdit();
  const navigateToNext = useNavigateToNextBookmark();
  const bookmarkPage = useBookmarkPageContent();

  const isArchived = viewMode === 'archive';
  const isActiveInReader =
    bookmarkPage?.activeBookmark != null &&
    bookmarkPage.readerLayout !== '2col' &&
    String(bookmarkPage.activeBookmark.id) === String(bookmark.id);

  const handleLike = () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    likeMutation.mutate(
      { bookmarkId: bookmark.id, action: newLikedState ? 'like' : 'unlike' },
      {
        onError: () => {
          setIsLiked(bookmark.liked);
        },
      },
    );
  };

  const handleArchive = () => {
    onClose();
    if (isActiveInReader) {
      navigateToNext(bookmark.id);
    }
    if (isArchived) {
      unarchiveMutation.mutate({ bookmarkId: bookmark.id });
    } else {
      archiveMutation.mutate({ bookmarkId: bookmark.id });
    }
  };

  const handleDelete = () => {
    if (window.confirm(t`Are you sure you want to delete this article?`)) {
      onClose();
      if (isActiveInReader) {
        navigateToNext(bookmark.id);
      }
      deleteMutation.mutate({ bookmarkId: bookmark.id });
    }
  };

  const handleMove = (folderId: string) => {
    // If this article is open in the reader, moving it to another section
    // removes it from the current list, so advance the reader to the next
    // article first (matches archive/delete).
    if (isActiveInReader) {
      navigateToNext(bookmark.id);
    }
    if (folderId === 'home') {
      // Moving to home uses the 'home' action, not 'move'
      unarchiveMutation.mutate({ bookmarkId: bookmark.id });
    } else {
      moveMutation.mutate({ bookmarkId: bookmark.id, folderId });
    }
  };

  if (showFolderMenu && userData?.folders) {
    return (
      <FolderMenu
        folders={userData.folders}
        currentFolderId={currentFolderId}
        viewMode={viewMode}
        onSelect={handleMove}
        onBack={() => setShowFolderMenu(false)}
      />
    );
  }

  if (showTagPopover) {
    return (
      <ReaderTagPopover
        bookmarkId={bookmark.id}
        initialBookmark={bookmark}
        closePopover={() => {
          setShowTagPopover(false);
          onClose();
        }}
      />
    );
  }

  return (
    <div className="w-44">
      <MenuItem
        onClick={handleLike}
        disabled={likeMutation.isPending}
        icon={<Icon name={isLiked ? 'like-filled' : 'like'} />}
      >
        {isLiked ? <Trans>Unlike</Trans> : <Trans>Like</Trans>}
      </MenuItem>

      <MenuItem onClick={() => setShowTagPopover(true)} icon={<Icon name="tag" />}>
        <Trans>Tag</Trans>
      </MenuItem>

      {userData?.folders && userData.folders.length > 0 && (
        <MenuItem
          onClick={() => setShowFolderMenu(true)}
          disabled={moveMutation.isPending}
          icon={<Icon name="folder" />}
        >
          <Trans>Move to folder</Trans>
        </MenuItem>
      )}

      {viewMode !== 'liked' && (
        <MenuItem
          onClick={handleArchive}
          disabled={archiveMutation.isPending || unarchiveMutation.isPending}
          icon={<Icon name="archive" />}
        >
          {isArchived ? <Trans>Unarchive</Trans> : <Trans>Archive</Trans>}
        </MenuItem>
      )}

      <MenuItem
        onClick={() => {
          openEditModal(bookmark);
          onClose();
        }}
        icon={<Icon name="edit" />}
      >
        <Trans>Edit</Trans>
      </MenuItem>

      <MenuItem
        onClick={handleDelete}
        disabled={deleteMutation.isPending}
        icon={<Icon name="trash" />}
      >
        <Trans>Delete</Trans>
      </MenuItem>
    </div>
  );
}

export default BookmarkActionsMenu;
